import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, describeError } from "@/lib/api";
import { useProviders } from "@/components/practice-setup/psPrimitives";

// Always/Never auto-detect from the first words of a typed rule.
export const NEVER_RE = /^\s*(never|don'?t|do not|no |avoid|exclude|stop)/i;

// Text-only code extraction (brief §2). CPT = 5 chars, ICD-10-CM shape, modifier after "modifier"/"mod".
const CPT_RE = /\b\d{4}[0-9A-Z]\b/g;
const ICD_RE = /\b[A-TV-Z]\d{2}(?:\.[0-9A-Z]{1,4})?\b/g;
const MOD_RE = /\b(?:modifier|mod)\s+([0-9A-Z]{2})\b/gi;

export function extractCodes(text) {
  const t = (text || "").toUpperCase();
  const cpts = t.match(CPT_RE) || [];
  const icds = t.match(ICD_RE) || [];
  const mods = [];
  let m;
  const mr = new RegExp(MOD_RE.source, "gi");
  while ((m = mr.exec(t))) mods.push(m[1]);
  const codes = [...new Set([...cpts, ...icds])];
  return { codes, cpts: [...new Set(cpts)], icds: [...new Set(icds)], mods: [...new Set(mods)] };
}

// Case / space / punctuation-normalised text for duplicate detection.
export function normalizeRule(text) {
  return (text || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// Does a rule mention any of the given claim codes?
export function ruleMentions(ruleText, codeSet) {
  return extractCodes(ruleText).codes.some((c) => codeSet.has(c));
}

/*
  Shared provider-rules state for the claim workspace. Reads inclusion_rules /
  exclusion_rules off the cached provider list (same react-query key as Practice
  Setup) and writes with a full-replace PATCH guarded by a re-GET-and-compare.
  Adding a rule never mutates the claim — it only writes the provider's rules.
*/
export function useProviderRules(npi) {
  const qc = useQueryClient();
  const { data: providers = [], isLoading } = useProviders();
  const provider = providers.find((p) => String(p.npi_number) === String(npi));
  const inList = !!provider;

  const [inc, setInc] = useState([]);
  const [exc, setExc] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [changed, setChanged] = useState(false);
  const loadedRef = useRef({ inc: [], exc: [] });
  const keyRef = useRef(null);

  // Reseed local state whenever the provider identity or its server rules change.
  useEffect(() => {
    if (!provider) { setInc([]); setExc([]); loadedRef.current = { inc: [], exc: [] }; keyRef.current = null; return; }
    const sInc = provider.inclusion_rules || [];
    const sExc = provider.exclusion_rules || [];
    const k = provider.npi_number + JSON.stringify(sInc) + JSON.stringify(sExc);
    if (keyRef.current !== k) {
      setInc(sInc); setExc(sExc); loadedRef.current = { inc: sInc, exc: sExc }; keyRef.current = k;
    }
  }, [provider]);

  const persist = async (nextInc, nextExc) => {
    if (!provider) return false;
    setSaving(true); setErr(""); setChanged(false);
    try {
      const fresh = (await api.get("/api/edi/note-to-claim/providers")).data?.providers ?? [];
      const server = fresh.find((p) => String(p.npi_number) === String(provider.npi_number));
      const sInc = server?.inclusion_rules || [], sExc = server?.exclusion_rules || [];
      const drifted = JSON.stringify(sInc) !== JSON.stringify(loadedRef.current.inc) || JSON.stringify(sExc) !== JSON.stringify(loadedRef.current.exc);
      if (drifted) {
        setInc(sInc); setExc(sExc); loadedRef.current = { inc: sInc, exc: sExc };
        setChanged(true);
        setErr("These rules changed somewhere else — reloaded the latest. Re-apply your edit.");
        setSaving(false);
        qc.invalidateQueries({ queryKey: ["providers"] });
        return false;
      }
      await api.patch(`/api/edi/note-to-claim/providers/${provider.npi_number}/billing-rules`, { inclusion_rules: nextInc, exclusion_rules: nextExc });
      loadedRef.current = { inc: nextInc, exc: nextExc };
      qc.invalidateQueries({ queryKey: ["providers"] });
      setSaving(false);
      return true;
    } catch (e) {
      setErr(describeError(e));
      setSaving(false);
      return false;
    }
  };

  const add = async (textRaw, kind) => {
    const t = (textRaw || "").trim();
    if (!t) return false;
    const nextInc = kind === "always" ? [...inc, t] : inc;
    const nextExc = kind === "never" ? [...exc, t] : exc;
    setInc(nextInc); setExc(nextExc);
    const ok = await persist(nextInc, nextExc);
    if (!ok) { setInc(loadedRef.current.inc); setExc(loadedRef.current.exc); }
    return ok;
  };

  const del = async (isInc, idx) => {
    const list = isInc ? inc : exc, other = isInc ? exc : inc;
    const next = list.filter((_, i) => i !== idx);
    if (isInc) setInc(next); else setExc(next);
    const ok = await persist(isInc ? next : other, isInc ? other : next);
    if (!ok) { setInc(loadedRef.current.inc); setExc(loadedRef.current.exc); }
    return ok;
  };

  const flip = async (fromInc, idx) => {
    const src = fromInc ? inc : exc, dst = fromInc ? exc : inc;
    const val = src[idx];
    const nextSrc = src.filter((_, i) => i !== idx);
    const nextDst = [...dst, val];
    if (fromInc) { setInc(nextSrc); setExc(nextDst); } else { setExc(nextSrc); setInc(nextDst); }
    const ok = await persist(fromInc ? nextSrc : nextDst, fromInc ? nextDst : nextSrc);
    if (!ok) { setInc(loadedRef.current.inc); setExc(loadedRef.current.exc); }
    return ok;
  };

  return { provider, inList, isLoading, inc, exc, add, del, flip, saving, err, changed };
}
